import { useState, useEffect, useRef, useCallback } from "react";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { VitrinCategory } from "@/models/home/VitrinCategory";
import { VitrinService } from "@/services/vitrin-service";
import SearchFilterBar, {
  DEFAULT_SORT,
  RELEVANCE_SORT,
  type FacetChipSpec,
} from "./search-filter-bar";
import CourseCard, { CourseCardSkeleton } from "@/features/miniapp/components/course-card";
import DotsLoading from "@/components/dots-loading";
import CategoryList2 from "./category-list2";
import { trackSearchPerformed } from "@/features/analytics";
import { useRecentSearches } from "../hooks/use-recent-searches";
import {
  booleanFacetCount,
  unionFacets,
  withLiveCounts,
  type SearchFacets,
} from "@/models/search/SearchFacets";
import {
  FACET_GROUPS,
  FACET_FILTER_KEYS,
} from "./facet-filter-drawer";

interface SearchClientProps {
  categories: VitrinCategory[];
  initialCategoryId?: number;
}

const PER_PAGE = 15;

// One column on phones, more as the rail widens — the card is a fixed-height
// row, so it tiles rather than stretching to fill a desktop-wide line.
const RESULTS_GRID = "grid grid-cols-1 gap-3 md:grid md:grid-cols-2 lg:grid-cols-3";

/**
 * Whether this API understands the search backend's newer filters.
 *
 * There is no capability endpoint to ask, but `facets` answers it for free: an
 * API that sends counts is an API that has the search backend, and one that
 * doesn't will reject `filter[has_certificate]` with a 400 rather than ignoring
 * it. So the first response that carries facets unlocks these filters, and
 * until then they are neither sent nor offered. Module scope so the answer
 * survives remounting between the search and category pages.
 */
let facetFiltersSupported = false;

// The toggles that ride on a boolean facet. `param` is the URL key, `facet` the
// group whose `true` bucket supplies the count.
const FACET_FILTERS = [
  {
    param: "cert",
    facet: "has_certificate",
    label: "گواهینامه‌دار",
    icon: "certificate",
  },
  {
    param: "quiz",
    facet: "has_quiz",
    label: "آزمون دارد",
    icon: "quiz",
  },
  {
    param: "online",
    facet: "has_online_class",
    label: "کلاس آنلاین",
    icon: "class-online",
  },
] as const;

/** A multi-select URL param: `?level=2,3` → `["2", "3"]`. */
function parseCsv(raw: string | null): string[] {
  if (!raw) return [];
  return raw.split(",").filter(Boolean);
}

function parseCategoryIds(raw: string | null): number[] {
  if (!raw) return [];
  return raw
    .split(",")
    .map((s) => parseInt(s, 10))
    .filter((n) => !Number.isNaN(n));
}

export default function SearchClient({
  categories,
  initialCategoryId,
}: SearchClientProps) {
  const [params, setParams] = useSearchParams();
  const { add: addRecentSearch } = useRecentSearches();

  // All search state lives in the URL so it survives navigation (going into a
  // course and pressing back restores the same query + filters).
  const activeQuery = (params.get("s") ?? "").trim();
  const selectedCategoryIds = parseCategoryIds(params.get("cat"));
  const discountOnly = params.get("discount") === "1";
  const freeOnly = params.get("free") === "1";

  // Latched once a response proves the API supports them; see the constant.
  const [facetFiltersReady, setFacetFiltersReady] = useState(
    facetFiltersSupported,
  );
  // A URL can carry these from a link shared off a newer API. Honour them only
  // where they work — sending one to an API without them fails the whole page.
  const activeFacetFilters = facetFiltersReady
    ? FACET_FILTERS.filter((f) => params.get(f.param) === "1")
    : [];

  // The list-shaped filters, same gate: each group's selected values by param.
  const facetSelections: Record<string, string[]> = Object.fromEntries(
    FACET_GROUPS.map((group) => [
      group.param,
      facetFiltersReady ? parseCsv(params.get(group.param)) : [],
    ]),
  );
  const facetSelectionCount = Object.values(facetSelections).reduce(
    (sum, values) => sum + values.length,
    0,
  );
  // The drawer also owns the boolean switches, so its badge counts those too.
  const drawerFilterCount =
    facetSelectionCount +
    activeFacetFilters.length +
    (freeOnly ? 1 : 0) +
    (discountOnly ? 1 : 0);

  // What to sort by when the user hasn't picked anything. Searching ranks by
  // relevance: forcing "newest" on top of a ranked result set throws the
  // ranking away, so the newest match wins over the best one. Browsing has no
  // query to rank against, so it keeps the curated newest-first order.
  const defaultSort = activeQuery ? RELEVANCE_SORT : DEFAULT_SORT;
  const sortOrder = params.get("sort") || defaultSort;

  // The typed text in the input (may differ from the submitted query).
  const [searchQuery, setSearchQuery] = useState<string>(activeQuery);

  // Keep the input in sync when the URL changes (e.g. back/forward).
  useEffect(() => {
    setSearchQuery(activeQuery);
  }, [activeQuery]);

  // Clearing the input returns to the categories view (clearing is not a search).
  useEffect(() => {
    if (!searchQuery.trim() && activeQuery) {
      setParams({}, { replace: true });
    }
  }, [searchQuery, activeQuery, setParams]);

  // Mutate the URL params while preserving the rest. `null` removes a key.
  const patchParams = useCallback(
    (updates: Record<string, string | null>) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(updates)) {
            if (value === null) next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  // Seed the initial category once (category page). After this the user can
  // toggle/clear it like any other filter; the ref stops it from re-applying.
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    seededRef.current = true;
    if (initialCategoryId && !params.get("cat")) {
      patchParams({ cat: String(initialCategoryId) });
    }
  }, [initialCategoryId, params, patchParams]);

  const handleSearch = (value: string) => {
    const q = value.trim();
    patchParams({ s: q || null });
  };

  const handleToggleCategory = (categoryId: number) => {
    const next = selectedCategoryIds.includes(categoryId)
      ? selectedCategoryIds.filter((id) => id !== categoryId)
      : [...selectedCategoryIds, categoryId];
    patchParams({ cat: next.length ? next.join(",") : null });
  };

  // Choosing the contextual default drops the key rather than pinning it, so
  // the sort keeps following the query the way it did before the user touched it.
  const handleSortChange = (value: string) =>
    patchParams({ sort: value === defaultSort ? null : value });

  const toggleFacetValue = (param: string, value: string) => {
    const current = facetSelections[param] ?? [];
    const next = current.includes(value)
      ? current.filter((item) => item !== value)
      : [...current, value];
    patchParams({ [param]: next.length ? next.join(",") : null });
  };

  // Clears everything the drawer can set — its groups and its switches.
  const clearFacetValues = () =>
    patchParams({
      free: null,
      discount: null,
      ...Object.fromEntries(FACET_FILTERS.map((f) => [f.param, null])),
      ...Object.fromEntries(FACET_GROUPS.map((g) => [g.param, null])),
    });

  const toggleDiscount = () =>
    patchParams({ discount: discountOnly ? null : "1" });

  const toggleFree = () => patchParams({ free: freeOnly ? null : "1" });

  const activeFilterCount =
    selectedCategoryIds.length +
    (discountOnly ? 1 : 0) +
    (freeOnly ? 1 : 0) +
    activeFacetFilters.length +
    facetSelectionCount;

  // Browsing is active when there's a text query OR any filter is set, so the
  // category page (filters only, no text) shows results just like a search.
  const hasActiveSearch = activeQuery.length > 0 || activeFilterCount > 0;

  const clearCategories = () => patchParams({ cat: null });

  const clearFilters = () =>
    patchParams({
      cat: null,
      discount: null,
      free: null,
      sort: null,
      ...Object.fromEntries(FACET_FILTERS.map((f) => [f.param, null])),
      ...Object.fromEntries(FACET_GROUPS.map((g) => [g.param, null])),
    });

  const {
    data,
    isLoading,
    isFetching,
    isPlaceholderData,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [
      "vitrin-search-courses",
      activeQuery,
      sortOrder,
      selectedCategoryIds,
      discountOnly,
      freeOnly,
      facetFiltersReady,
      activeFacetFilters.map((f) => f.param).join(","),
      facetSelections,
    ],
    enabled: hasActiveSearch,
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      VitrinService.getCourses({
        filter: {
          global: activeQuery,
          ...(selectedCategoryIds.length > 0 && {
            category_id: selectedCategoryIds,
          }),
          // The discount filter asks the backend for any non-zero discount.
          ...(discountOnly && { discount_min: 1 }),
          // `is_free` is the filter that means this; `price_max: 0` matches on
          // the *effective* price, so it also sweeps in a paid course that a
          // 100% discount has zeroed out today. Use the accurate one wherever
          // the API knows it, and the approximation everywhere else.
          ...(freeOnly &&
            (facetFiltersReady ? { is_free: true } : { price_max: 0 })),
          ...Object.fromEntries(
            activeFacetFilters.map((f) => [f.facet, true]),
          ),
          // Multi-select groups go as CSV; the service joins the list. `level`
          // and the id filters are numeric server-side, but the API parses the
          // digits out of the string, so the URL values pass straight through.
          ...Object.fromEntries(
            FACET_GROUPS.filter(
              (group) => (facetSelections[group.param] ?? []).length > 0,
            ).map((group) => [
              FACET_FILTER_KEYS[group.facet],
              facetSelections[group.param],
            ]),
          ),
        },
        // Omitted rather than sent as `relevance`: omitting *is* relevance on
        // the search backend, and the deployed one 400s on a sort key it
        // doesn't know. Omitting leaves it on its curated default there.
        sort: sortOrder === RELEVANCE_SORT ? undefined : sortOrder,
        page: pageParam as number,
        per_page: PER_PAGE,
      }),
    getNextPageParam: (lastPage) => {
      const pagination = lastPage.pagination;
      if (!pagination) return undefined;
      return pagination.current_page < pagination.last_page
        ? pagination.current_page + 1
        : undefined;
    },
    // Changing a filter changes the query key, and without this the hook would
    // hand back `data: undefined` until the new page lands. That empties
    // `facets`, which unmounts the filter drawer mid-interaction and loses the
    // open state — the drawer would shut the moment you ticked something.
    // Keeping the previous page also stops the results flashing to skeletons on
    // every toggle; the counts are momentarily stale instead, which is the
    // better trade.
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 5,
  });

  const courses = data?.pages.flatMap((pg) => pg.data ?? []) ?? [];
  const total = data?.pages[0]?.pagination?.total ?? courses.length;

  // Counts for the filter UI. Absent whenever the API has no search backend or
  // its cluster is down, which is a normal answer: the filters still render,
  // just without numbers. Every page carries the same block; the first will do.
  const liveFacets = data?.pages[0]?.facets ?? null;

  // The API drops a bucket as soon as nothing matches it, so each filter the
  // user adds deletes options — and whole groups — out from under them. Worse,
  // a group they had already picked in could disappear, stranding a filter that
  // still affects the results with no way left to clear it.
  //
  // So accumulate every option seen for this search and re-attach the live
  // counts, letting an option that no longer matches read zero instead of
  // vanishing. The text query defines the universe: a new search starts over.
  const seenFacetsRef = useRef<{ query: string; facets: SearchFacets } | null>(
    null,
  );
  if (liveFacets) {
    const previous =
      seenFacetsRef.current?.query === activeQuery
        ? seenFacetsRef.current.facets
        : null;
    seenFacetsRef.current = {
      query: activeQuery,
      facets: unionFacets(previous, liveFacets),
    };
  }
  const facets = liveFacets
    ? withLiveCounts(seenFacetsRef.current?.facets ?? null, liveFacets)
    : null;

  const freeCount = booleanFacetCount(facets?.is_free, true);

  // Facets arriving is the proof that the newer filters will be accepted.
  useEffect(() => {
    if (!facets || facetFiltersReady) return;
    facetFiltersSupported = true;
    setFacetFiltersReady(true);
  }, [facets, facetFiltersReady]);

  const facetChips: FacetChipSpec[] = facetFiltersReady
    ? FACET_FILTERS.map((f) => ({
        key: f.param,
        label: f.label,
        icon: f.icon,
        active: params.get(f.param) === "1",
        count: booleanFacetCount(facets?.[f.facet], true),
        onToggle: () =>
          patchParams({ [f.param]: params.get(f.param) === "1" ? null : "1" }),
      }))
    : [];


  const trackedQueryRef = useRef<string | null>(null);
  useEffect(() => {
    if (!activeQuery || isLoading || isError) return;
    if (trackedQueryRef.current === activeQuery) return;
    trackedQueryRef.current = activeQuery;
    addRecentSearch(activeQuery);
    trackSearchPerformed({ query: activeQuery, results_count: total });
  }, [activeQuery, isLoading, isError, total, addRecentSearch]);

  // Infinite scroll: fetch the next page when the sentinel enters the viewport.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, courses.length]);

  const showCategories = !hasActiveSearch;
  const showInitialLoading = hasActiveSearch && isLoading;
  const noResults = hasActiveSearch && !isLoading && !isError && courses.length === 0;
  // Header copy: name the query when searching, otherwise a plain course count.
  const resultsHeading = activeQuery
    ? `${total.toLocaleString("fa-IR")} نتیجه برای «${activeQuery}»`
    : `${total.toLocaleString("fa-IR")} دوره`;

  return (
    <div className="w-full grow">
      <SearchFilterBar
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onSubmit={handleSearch}
        loading={isFetching}
        showFilters={hasActiveSearch}
        categories={categories}
        selectedCategoryIds={selectedCategoryIds}
        onToggleCategory={handleToggleCategory}
        onClearCategories={clearCategories}
        sortOrder={sortOrder}
        onSortChange={handleSortChange}
        discountOnly={discountOnly}
        onToggleDiscount={toggleDiscount}
        freeOnly={freeOnly}
        onToggleFree={toggleFree}
        freeCount={freeCount}
        categoryCounts={facets?.category}
        facetChips={facetChips}
        facetToggles={[
          {
            key: "free",
            label: "فقط دوره‌های رایگان",
            active: freeOnly,
            count: freeCount,
            onToggle: toggleFree,
          },
          {
            key: "discount",
            label: "فقط دوره‌های تخفیف‌دار",
            active: discountOnly,
            onToggle: toggleDiscount,
          },
          ...facetChips.map((chip) => ({
            key: chip.key,
            label: chip.label,
            active: chip.active,
            count: chip.count,
            onToggle: chip.onToggle,
          })),
        ]}
        facets={facets}
        facetSelections={facetSelections}
        onToggleFacetValue={toggleFacetValue}
        onClearFacetValues={clearFacetValues}
        facetSelectionCount={drawerFilterCount}
      />

      {showCategories && <CategoryList2 categories={categories} />}

      {showInitialLoading && (
        <div className={`p-4 ${RESULTS_GRID}`}>
          {Array.from({ length: 6 }).map((_, index) => (
            <CourseCardSkeleton key={index} />
          ))}
        </div>
      )}

      {hasActiveSearch && !isLoading && !isError && courses.length > 0 && (
        <div className="p-4">
          <h2 className="text-xs font-semibold text-default-600 mb-3">
            {resultsHeading}
          </h2>
          <div
            className={`${RESULTS_GRID} transition-opacity ${
              isPlaceholderData ? "opacity-60" : "opacity-100"
            }`}
            data-track-section="search_results"
          >
            {courses.map((course, index) => (
              <CourseCard
                key={course.id}
                slotIndex={index}
                title={course.title}
                sessionsCount={course.lessons_count}
                studentsCount={course.users_count}
                thumbnail={course.vitrin_cover_url || course.cover_url}
                courseId={course.id}
                miniApp={course.mini_app}
                description={course.description}
                rating={course.ratings_avg}
                price={course.price}
                specialPrice={course.special_price}
                isFree={course.is_free}
              />
            ))}
          </div>

          {/* Infinite scroll sentinel + loader */}
          {hasNextPage && (
            <div ref={sentinelRef} className="py-4">
              {isFetchingNextPage && <DotsLoading />}
            </div>
          )}
        </div>
      )}

      {noResults && (
        <div className="text-center py-12 px-4 text-default-600">
          <p className="text-sm">
            {activeQuery
              ? `نتیجه‌ای برای «${activeQuery}» یافت نشد`
              : "دوره‌ای یافت نشد"}
          </p>
          {activeFilterCount > 0 && (
            <button
              type="button"
              onClick={clearFilters}
              className="mt-3 text-xs font-medium text-primary"
            >
              حذف فیلترها
            </button>
          )}
        </div>
      )}

      {hasActiveSearch && !isLoading && isError && (
        <div className="text-center py-12 text-default-600">خطا در جستجو</div>
      )}
    </div>
  );
}
